import React, { useState } from 'react';
import { func, bool, object } from 'prop-types';
import { intlShape, FormattedMessage } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { types as sdkTypes } from '../../../../util/sdkLoader';

import {
  declineProposal,
  acceptProposal,
  counterProposal,
  acceptCounterProposal,
  declineCounterProposal,
} from '../../../../ducks/listing.duck';
import { manageDisableScrolling } from '../../../../ducks/UI.duck';
import { withViewport } from '../../../../util/contextHelpers';
import { propTypes } from '../../../../util/types';
import { ensureTransaction } from '../../../../util/data';
import {
  getProposalAmountWithoutProviderFee,
  getUserTxRole,
  TRANSITION_COUNTER_PROPOSAL,
  TRANSITION_PROPOSAL,
  txRoleIsProvider,
} from '../../../../util/transactions';
import {
  PrimaryButton,
  SecondaryButton,
  IconQuestion,
  UserDisplayName,
  ModalSwimmy,
  FieldTextInput,
  Form,
} from '../../../../components';
import * as validators from '../../../../util/validators';

import { useHistory } from 'react-router-dom';

import css from './Pending.module.css';
import { formatMoney } from '../../../../util/currency';
import { path } from 'ramda';

const { Money } = sdkTypes;

const Pending = (props) => {
  const {
    transaction,
    currentUser,
    intl,
    onManageDisableScrolling,
    onDeclineProposal,
    onAcceptProposal,
    onCounterProposal,
    onAcceptCounterProposal,
    onDeclineCounterProposal,
    declineProposalInProgress,
    declineProposalError,
    acceptProposalInProgress,
    acceptProposalError,
    counterProposalInProgress,
    counterProposalError,
    acceptCounterProposalInProgress,
    acceptCounterProposalError,
    declineCounterProposalInProgress,
    declineCounterProposalError,
  } = props;

  const [isOpenCounterProposalModal, setIsOpenCounterProposalModal] = useState(false);
  const history = useHistory();
  const currentTransaction = ensureTransaction(transaction);

  const transactionId = currentTransaction.id;
  const customer = currentTransaction.customer;
  const provider = currentTransaction.provider;
  const ownRole = getUserTxRole(currentUser.id, currentTransaction);

  const isPendingProvider =
    ownRole === 'provider' && currentTransaction.attributes.lastTransition === TRANSITION_PROPOSAL;
  const isPendingCustomer =
    ownRole === 'customer' &&
    currentTransaction.attributes.lastTransition === TRANSITION_COUNTER_PROPOSAL;

  const usersName = txRoleIsProvider(ownRole) ? (
    <UserDisplayName user={provider} intl={intl} />
  ) : (
    <UserDisplayName user={customer} intl={intl} />
  );
  const displayName = usersName;
  const pendingMessage = (
    <FormattedMessage id="ActivityFeed.pendingProposal" values={{ displayName }} />
  );
  const declineMessage = intl.formatMessage({ id: 'ActivityFeed.pendingProposalDecline' });
  const acceptMessage = intl.formatMessage({ id: 'ActivityFeed.pendingProposalAccept' });
  const counterProposalMessage = intl.formatMessage({
    id: 'ActivityFeed.pendingProposalCounterProposal',
  });
  const ErrorText = intl.formatMessage({ id: 'ActivityFeed.pendingProposalError' });

  const currency = path(
    ['attributes', 'protectedData', 'proposalPriceCurrency'],
    currentTransaction
  );
  const proposalPriceAmount = getProposalAmountWithoutProviderFee(path(
    ['attributes', 'protectedData', 'proposalPriceAmount'],
    currentTransaction
  ));

  const minimumCounterProposalAmountFormatted = formatMoney(
    intl,
    new Money(proposalPriceAmount, currency)
  );

  const declineInProgress = isPendingProvider
    ? declineProposalInProgress
    : declineCounterProposalInProgress;
  const acceptInProgress = isPendingProvider
    ? acceptProposalInProgress
    : acceptCounterProposalInProgress;

  const accept = async () => {
    const protectedData = path(
      ['attributes', 'protectedData'],
      currentTransaction
    );
    const listingId = path(
      ['listing', 'id', 'uuid'],
      currentTransaction
    );
    const orderParams = getOrderParams({
      protectedData,
      listingId,
    });
    if (isPendingProvider) {
      await onAcceptProposal(transactionId, orderParams);
    }
    if (isPendingCustomer) {
      await onAcceptCounterProposal(transactionId, orderParams);
    }
    if (isPendingProvider || isPendingCustomer) {
      history.push(0);
    }
  };

  const decline = () => {
    let promise;
    if (isPendingProvider) {
      promise = onDeclineProposal(transactionId);
    }
    if (isPendingCustomer) {
      promise = onDeclineCounterProposal(transactionId);
    }
    if (promise) {
      promise.then(() => history.push(0)).catch(() => {});
    }
  };

  const inProgress =
    declineInProgress ||
    acceptInProgress ||
    counterProposalInProgress ||
    acceptCounterProposalInProgress ||
    declineCounterProposalInProgress;
  const isError =
    declineProposalError ||
    acceptProposalError ||
    counterProposalError ||
    acceptCounterProposalError ||
    declineCounterProposalError;

  if (!isPendingProvider && !isPendingCustomer) {
    return null;
  }

  return (
    <>
      <ModalSwimmy
        intl={intl}
        isOpen={isOpenCounterProposalModal}
        onClose={() => setIsOpenCounterProposalModal(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        content={
          <FinalForm
            initialValues={{ counterProposalAmountUserInput: '' }}
            onSubmit={({ counterProposalAmountUserInput }) => {
              onCounterProposal(transactionId, counterProposalAmountUserInput * 100)
                .then(() => history.push(0))
                .catch(() => {});
            }}
            render={(formRenderProps) => {
              const { handleSubmit, invalid, pristine } = formRenderProps;
              const submitDisabled = invalid || pristine || counterProposalInProgress;

              return (
                <Form id="counterProposalForm" onSubmit={handleSubmit}>
                  <FieldTextInput
                    className={css.field}
                    type="number"
                    name="counterProposalAmountUserInput"
                    id="counterProposalAmountUserInput"
                    label={intl.formatMessage(
                      { id: 'ActivityFeed.counterProposalAmountInCurrency' },
                      { currency }
                    )}
                    placeholder={intl.formatMessage(
                      { id: 'ActivityFeed.enterAmountInCurrency' },
                      { currency }
                    )}
                    validate={validators.isNumberMoreThan(
                      intl.formatMessage(
                        { id: 'ActivityFeed.enterNumberSuperiorTo' },
                        { formattedAmount: minimumCounterProposalAmountFormatted }
                      ),
                      proposalPriceAmount / 100
                    )}
                  />

                  <div style={{ height: 10 }} />

                  <div className={css.buttonContainer}>
                    <PrimaryButton
                      className={css.pendingDetailsButton}
                      type="submit"
                      form="counterProposalForm"
                      inProgress={counterProposalInProgress}
                      disabled={submitDisabled}
                    >
                      {intl.formatMessage({ id: 'ActivityFeed.sendCounterProposal' })}
                    </PrimaryButton>
                  </div>
                </Form>
              );
            }}
          />
        }
      />
      <div className={css.pending}>
        <IconQuestion className={css.pendingImg} />
        <p className={css.pendingContent}>{pendingMessage}</p>
        {isError && <span className={css.error}>{ErrorText}</span>}
        <div className={css.pendingButtons}>
          <PrimaryButton
            className={css.pendingDetailsButton}
            onClick={accept}
            inProgress={acceptInProgress}
            disabled={inProgress}
          >
            {acceptMessage}
          </PrimaryButton>
          {isPendingProvider ? (
            <PrimaryButton
              className={css.pendingDetailsButton}
              onClick={() => setIsOpenCounterProposalModal(true)}
              inProgress={counterProposalInProgress || isOpenCounterProposalModal}
              disabled={inProgress}
            >
              {counterProposalMessage}
            </PrimaryButton>
          ) : null}
          <SecondaryButton
            className={css.pendingDetailsButton}
            onClick={decline}
            inProgress={declineInProgress}
            disabled={inProgress}
          >
            {declineMessage}
          </SecondaryButton>
        </div>
      </div>
    </>
  );
};

Pending.defaultProps = {
  transition: null,
  transaction: null,
  currentUser: null,
  intl: null,
  onOpenReviewModal: null,
  declineProposalError: null,
  acceptProposalError: null,
  counterProposalError: null,
};

Pending.propTypes = {
  transition: propTypes.transition.isRequired,
  transaction: propTypes.transaction.isRequired,
  currentUser: propTypes.currentUser.isRequired,
  intl: intlShape.isRequired,
  onManageDisableScrolling: func.isRequired,
  onOpenReviewModal: func.isRequired,
  onDeclineProposal: func.isRequired,
  onAcceptProposal: func.isRequired,
  onCounterProposal: func.isRequired,
  publicData: object.isRequired,
  declineProposalInProgress: bool.isRequired,
  declineProposalError: propTypes.error,
  acceptProposalInProgress: bool.isRequired,
  acceptProposalError: propTypes.error,
  counterProposalInProgress: bool.isRequired,
  counterProposalError: propTypes.error,
  acceptCounterProposalInProgress: bool.isRequired,
  acceptCounterProposalError: propTypes.error,
  declineCounterProposalInProgress: bool.isRequired,
  declineCounterProposalError: propTypes.error,
};

const mapDispatchToProps = (dispatch) => ({
  onDeclineProposal: (transactionId) => dispatch(declineProposal(transactionId)),
  onAcceptProposal: (transactionId, orderParams) => dispatch(acceptProposal(transactionId, orderParams)),
  onAcceptCounterProposal: (transactionId, orderParams) => dispatch(acceptCounterProposal(transactionId, orderParams)),
  onDeclineCounterProposal: (transactionId) => dispatch(declineCounterProposal(transactionId)),
  onCounterProposal: (transactionId, counterProposalAmount) =>
    dispatch(counterProposal(transactionId, counterProposalAmount)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const mapStateToProps = (state) => {
  const {
    id,
    declineProposalInProgress,
    declineProposalError,
    acceptProposalInProgress,
    acceptProposalError,
    counterProposalInProgress,
    counterProposalError,
    acceptCounterProposalInProgress,
    acceptCounterProposalError,
    declineCounterProposalInProgress,
    declineCounterProposalError,
  } = state.listing;
  const { currentUser } = state.user;

   return {
    currentUser,
    listingId: id,
    declineProposalInProgress,
    declineProposalError,
    acceptProposalInProgress,
    acceptProposalError,
    counterProposalInProgress,
    counterProposalError,
    acceptCounterProposalInProgress,
    acceptCounterProposalError,
    declineCounterProposalInProgress,
    declineCounterProposalError,
  };
};

const getOrderParams = (bookingParams) => {
  const {
    listingId,
    protectedData,
  } = bookingParams;
  return {
    listingId,
    bookingStart: protectedData?.date,
    bookingEnd: protectedData?.endDate,
    protectedData,
    quantity1: protectedData?.quantity?.adults,
    quantity2: protectedData?.quantity?.children,
    quantity3: protectedData?.quantity?.infants,
    quantityHours: protectedData?.quantityHours,
  };
}

export default compose(withViewport, connect(mapStateToProps, mapDispatchToProps))(Pending);
